import { SeatingTabSeat } from 'Services/Api/_HumanWritten/SeatingService';
import { buildSeatPanels, SeatPanel } from 'Modules/Ferries/Utils/seatGrid';

/** One block of the deck the picker draws as a unit. */
export type SeatingDeckPanel = SeatPanel<SeatingTabSeat>;

/**
 * Splits the seats into the blocks the picker draws.
 *
 * <p>The same grouping the admin layout uses, so the booker sees the deck the admin drew: by the
 * vessel's own sections where it has them, by side where it does not, and as one block otherwise.</p>
 */
export const buildDeckPanels = (seats: SeatingTabSeat[]): SeatingDeckPanel[] => buildSeatPanels(seats);

/**
 * Whether this seat can be given to the passenger currently being seated.
 *
 * <p>A seat is offered when it is an ordinary seat, free, not already taken by someone else on the
 * same booking, and carried by the passenger's class — or unclassed, which is sellable to
 * anyone.</p>
 */
export const isSeatSelectable = (
	seat: SeatingTabSeat,
	passengerClassId: string | undefined,
	seatIdsTakenOnThisBooking: string[],
): boolean => {
	// A wheelchair bay holds a chair, not a person. It is drawn so the plan matches the vessel, but
	// choosing one would sell a place nobody can sit in.
	if (seat.kind && seat.kind !== 'SEAT') {
		return false;
	}
	if (seat.occupied || seatIdsTakenOnThisBooking.includes(seat.id)) {
		return false;
	}
	if (seat.passengerClassIds.length === 0) {
		return true;
	}
	return !!passengerClassId && seat.passengerClassIds.includes(passengerClassId);
};

/** What a seat is to the passenger currently being seated. */
export type SeatState =
	| 'SELECTED'
	| 'TAKEN_BY_THIS_BOOKING'
	| 'TAKEN'
	| 'NOT_A_SEAT'
	| 'NOT_IN_YOUR_CLASS'
	| 'AVAILABLE';

/**
 * Which of the four states a seat is in, in the order they take precedence.
 *
 * <p>Three kinds of "you cannot have this", because to a booker they are three different facts and
 * only one of them is permanent. A seat <b>held by a companion</b> is undone by changing their mind;
 * a seat <b>not in their class</b> is undone by changing class; a seat that is <b>taken</b> is gone
 * whatever they do. Collapsing them into one state made every refusal look the same.</p>
 */
export const seatState = (
	seat: SeatingTabSeat,
	passengerClassId: string | undefined,
	seatIdsTakenOnThisBooking: string[],
	selectedSeatId: string | undefined,
): SeatState => {
	if (seat.id === selectedSeatId) {
		return 'SELECTED';
	}
	if (seatIdsTakenOnThisBooking.includes(seat.id)) {
		return 'TAKEN_BY_THIS_BOOKING';
	}
	if (seat.occupied) {
		return 'TAKEN';
	}
	// Before the class check, because a wheelchair bay is not a seat this booker is being refused —
	// it is not a seat. It read as "not in your class", which invites them to change class to get it,
	// and no class sells it.
	if (seat.kind && seat.kind !== 'SEAT') {
		return 'NOT_A_SEAT';
	}
	return isSeatSelectable(seat, passengerClassId, seatIdsTakenOnThisBooking)
		? 'AVAILABLE'
		: 'NOT_IN_YOUR_CLASS';
};

/**
 * The classes worth putting in the plan's key: those the vessel actually has seats for.
 *
 * <p>A class with no seats is real — it is unreserved seating with a headcount and a fare — but its
 * colour never appears on the plan, so listing it in the key sends the booker looking for something
 * that is not drawn.</p>
 */
export const classesWithSeats = <T extends { id: string }>(
	passengerClasses: T[],
	seats: SeatingTabSeat[],
): T[] =>
	passengerClasses.filter(passengerClass =>
		seats.some(seat => seat.passengerClassIds.includes(passengerClass.id)),
	);

/**
 * A class's description, split into the points it is made of.
 *
 * <p>The field is one free-text string and the form is a textarea, so an operator writing a class's
 * perks does the obvious thing and puts one per line. Rendered into a single element those newlines
 * collapse and five selling points run together into an unreadable sentence — which is what happens
 * to "Free seat selection / Upper deck / Priority boarding" today.</p>
 *
 * <p>Blank lines are dropped rather than rendered as gaps, so the spacing a writer leaves between
 * points in the textarea does not become empty bullets. A description with no newlines comes back as
 * a single entry, which callers render as a plain line rather than a one-item list.</p>
 */
export const describeClassPoints = (description?: string | null): string[] =>
	(description ?? '')
		.split(/\r?\n/)
		// Leading list markers are stripped: an operator who types "- Free seat selection" means the
		// point, not the hyphen, and the list draws its own markers.
		.map(line => line.trim().replace(/^[-•*]\s*/, '').trim())
		.filter(line => line.length > 0);

/** One passenger waiting for a seat. */
export interface SeatRequest {
	passengerId: string;
	passengerClassId?: string;
}

/**
 * Seats the whole party before the booker is asked to.
 *
 * <p>Most bookers do not care which seat they get, they care that their party is together — so the
 * step arrives with seats already chosen and the booker changes them only if they want to. What has
 * to be right is the togetherness: three people scattered across the deck is worse than no
 * suggestion at all, because the booker then has to undo it.</p>
 *
 * <p>So the search is <b>block first</b>. For each class it looks for a run of adjacent seats in one
 * row long enough for everyone in it; failing that, the largest runs it can find, preferring rows in
 * a section the party is already sitting in; failing that, whatever is left. A party split across
 * classes is seated per class — a Premium passenger cannot sit in the Comfort cabin however much
 * their companions would like them to — but each class prefers a section the others are already in,
 * so the blocks end up as close as the vessel allows.</p>
 *
 * <p>Never assigns a seat someone else holds, one already given out in this pass, or a wheelchair
 * bay: a bay is a place for a chair and has to be chosen deliberately rather than handed to whoever
 * booked first. Passengers it cannot seat are left unseated rather than seated badly.</p>
 */
export const autoAssignSeats = (
	requests: SeatRequest[],
	seats: SeatingTabSeat[],
	seatIdsTakenOnThisBooking: string[] = [],
): Record<string, SeatingTabSeat> => {
	const assigned: Record<string, SeatingTabSeat> = {};
	if (requests.length === 0) {
		return assigned;
	}

	const claimed = [...seatIdsTakenOnThisBooking];
	// Sections this booking is already sitting in, so a second class is drawn towards the first.
	const preferredSections = new Set<string>();

	const byClass = new Map<string, SeatRequest[]>();
	requests.forEach(request => {
		const key = request.passengerClassId ?? '';
		byClass.set(key, [...(byClass.get(key) ?? []), request]);
	});

	// Largest group first: the hardest party to seat together should get first pick of the long runs,
	// or a pair takes the middle of the only row that would have held four.
	[...byClass.entries()]
		.sort((a, b) => b[1].length - a[1].length)
		.forEach(([classId, group]) => {
			const passengerClassId = classId === '' ? undefined : classId;

			const picked = pickBlock(
				group.length,
				seats.filter(seat => isSeatSelectable(seat, passengerClassId, claimed)),
				preferredSections,
			);

			picked.forEach((seat, index) => {
				const request = group[index];
				if (!request) {
					return;
				}
				assigned[request.passengerId] = seat;
				claimed.push(seat.id);
				preferredSections.add(seat.section ?? '');
			});
		});

	return assigned;
};

/**
 * The best `wanted` seats out of what is free: a single adjacent run where one exists, otherwise the
 * longest runs available, preferring sections the party is already in.
 */
const pickBlock = (
	wanted: number,
	available: SeatingTabSeat[],
	preferredSections: Set<string>,
): SeatingTabSeat[] => {
	const runs = adjacentRuns(available);

	// Best candidate first: a run that fits the whole group beats one that does not, then a preferred
	// section, then the tightest fit — taking a run of six for a pair breaks up a block a later party
	// of six would have wanted.
	runs.sort((a, b) => {
		const aFits = a.length >= wanted ? 0 : 1;
		const bFits = b.length >= wanted ? 0 : 1;
		if (aFits !== bFits) {
			return aFits - bFits;
		}

		const aPreferred = preferredSections.has(a[0].section ?? '') ? 0 : 1;
		const bPreferred = preferredSections.has(b[0].section ?? '') ? 0 : 1;
		if (aPreferred !== bPreferred) {
			return aPreferred - bPreferred;
		}

		// Among runs that fit, the smallest; among runs that do not, the largest.
		return aFits === 0 ? a.length - b.length : b.length - a.length;
	});

	const picked: SeatingTabSeat[] = [];
	runs.some(run => {
		run.slice(0, wanted - picked.length).forEach(seat => picked.push(seat));
		return picked.length >= wanted;
	});

	return picked;
};

/**
 * The free seats broken into runs of neighbours.
 *
 * <p>Neighbours within one row of one section, in the order the deck draws them. A gap in the middle
 * of a row — an aisle, a seat someone else holds — ends the run, which is the point: two seats with
 * a stranger between them are not two seats together.</p>
 */
const adjacentRuns = (available: SeatingTabSeat[]): SeatingTabSeat[][] => {
	const byRow = new Map<string, SeatingTabSeat[]>();
	available.forEach(seat => {
		// JSON rather than a delimiter, because a section name may contain any character a delimiter
		// could be.
		const key = JSON.stringify([seat.section ?? '', seat.rowLabel]);
		byRow.set(key, [...(byRow.get(key) ?? []), seat]);
	});

	const runs: SeatingTabSeat[][] = [];

	byRow.forEach(rowSeats => {
		const ordered = [...rowSeats].sort(
			(a, b) => (a.order ?? 0) - (b.order ?? 0) || a.seatNumber.localeCompare(b.seatNumber),
		);

		let run: SeatingTabSeat[] = [];
		ordered.forEach((seat, index) => {
			const previous = ordered[index - 1];
			// A jump of more than one in the deck's own order means something sits between them.
			const adjacent =
				previous != null
				&& previous.order != null
				&& seat.order != null
				&& seat.order - previous.order === 1;

			if (!adjacent && run.length > 0) {
				runs.push(run);
				run = [];
			}
			run.push(seat);
		});

		if (run.length > 0) {
			runs.push(run);
		}
	});

	return runs;
};

/**
 * A seat as the booker reads it — "12A", not "U12A".
 *
 * <p>The stored label is prefixed with the deck's code, because seat numbers repeat between decks
 * and a deckhand holding a ticket has no plan in front of them to say which 12A was meant. A booker
 * does: the deck is the tab they are looking at. So the prefix is dropped here and kept everywhere
 * the label travels alone — the ticket, the booking note, the staff views, all of which build it
 * server-side from <c>SeatLabels.Qualified</c>.</p>
 */
export const shortSeatLabel = (seat: { rowLabel: string; seatNumber: string }): string =>
	`${seat.rowLabel}${seat.seatNumber}`;

/**
 * A seat with the deck it is on — "Upper deck · 12A" — written the way the server writes it
 * (`SeatLabels.WithDeck`), so a seat picked on this step reads the same as one loaded from the booking.
 */
export const deckSeatLabel = (deckName: string | null | undefined, seat: { rowLabel: string; seatNumber: string }): string =>
	(deckName?.trim() ? `${deckName.trim()} · ${shortSeatLabel(seat)}` : shortSeatLabel(seat));
